import {
  forwardRef,
  type HTMLAttributes,
  useContext,
  useEffect,
  useState,
} from 'react';
import cn from 'classnames';

import { playSound } from '@/common/lib/soundEffect';
import { ClaimCashCardAnimation } from '@/components/Animations/ClaimItemAnimation/ClaimCashCardAnimation/ClaimCashCardAnimation';
import { StringXSmall } from '@/components/Typography/Typography';
import { MotionPicture } from '@/components/ui/Picture/Picture';
import { selectMiniGameReward } from '@/redux/selectors/diceGameSelectors';
import { useAppSelector } from '@/redux/store';

import { DiceGameContext } from '../../context';
import { Tile } from '../Tile/Tile';
import { TilesBg, type TilesBgVariantType } from '../TilesBg/TilesBg';

import css from './styles.module.scss';

import starPng from '/img/icons/star-large.png';
import starWebp from '/img/icons/star-large.webp';

interface StarsTileProps extends HTMLAttributes<HTMLDivElement> {
  isActive?: boolean;
  isSuperGame?: boolean;
  value?: number;
  variant?: 'mini-game' | 'start' | 'default';
}

const StarsMiniGameTile = forwardRef<HTMLDivElement, StarsTileProps>(
  ({ isSuperGame, value, variant, ...props }, ref) => {
    const starFinalPositon = useAppSelector(
      (state) => state.diceGame.position.rewards.str,
    );
    const reward = useAppSelector(selectMiniGameReward);
    const [animationFinished, setAnimationFinished] = useState(false);
    const { setIsRewardScaled } = useContext(DiceGameContext);

    const isAnimating =
      props.isActive && isSuperGame && !!reward && !animationFinished;

    useEffect(() => {
      if (isAnimating && (value !== 0 || variant !== 'mini-game')) {
        setIsRewardScaled(true);
      }
    }, [isAnimating, value]);

    useEffect(() => {
      if (value && props.isActive && isAnimating) playSound('win/flow');
    }, [props.isActive]);

    const getTilesBgVariant = (): TilesBgVariantType => {
      if (value === 0 && variant === 'mini-game') {
        return 'white';
      }

      if (props.isActive) {
        return 'purple-active';
      }

      return isSuperGame ? 'purple-fill' : 'purple';
    };

    return (
      <Tile
        ref={ref}
        {...props}
        variant={props.isActive ? 'dark' : 'grey'}
        className={cn(
          props.className,
          'flex items-center justify-center !bg-transparent',
        )}
      >
        {value === 0 && variant === 'mini-game' ? (
          <span
            className={cn(
              'font-black text-[2rem] text-[#F0F0F0] leading-none relative z-[2]',
              css.zeroShadow,
            )}
          >
            0
          </span>
        ) : (
          <div className="relative z-[2] size-[72%] flex">
            <MotionPicture
              className="w-full h-full"
              img={starPng}
              webp={starWebp}
              alt="tgem"
              animate={{ scale: isAnimating ? 1.2 : 1 }}
              transition={{
                duration: 0.3,
                delay: 0,
              }}
            />
            {isAnimating && (
              <ClaimCashCardAnimation
                icon={{ img: starPng, webp: starWebp }}
                reward="str"
                finalPositon={starFinalPositon}
                type="arc"
                onAnimationEnd={() => {
                  setIsRewardScaled(false);
                  setAnimationFinished(true);
                }}
                scale={1.4}
                startScale={1.2}
              />
            )}
          </div>
        )}
        {!!value && (
          <div className={css.tileValue}>
            <StringXSmall weight="bold">{value}</StringXSmall>
          </div>
        )}
        <TilesBg
          className={cn(!props.isActive && '!top-0 !left-0 !w-full !h-full')}
          variant={getTilesBgVariant()}
          withStarsBg={props.isActive && isSuperGame && value !== 0}
        />
      </Tile>
    );
  },
);

export { StarsMiniGameTile };
